import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Plus, Search, User, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);

  const isAdmin = user?.role === 'Administrador';

  const loadDocuments = async (deleted) => {
    try {
      const params = deleted ? '?status=Excluído' : '';
      const response = await axios.get(`${API_URL}/documents${params}`, { withCredentials: true });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments(showDeleted);
  }, [showDeleted]);

  const getStatusColor = (status) => {
    const colors = {
      'Rascunho': 'bg-[#8E8E93] text-white',
      'Em Revisão': 'bg-[#FF9F0A] text-white',
      'Aprovado': 'bg-[#007AFF] text-white',
      'Finalizado': 'bg-[#34C759] text-white',
      'Arquivado': 'bg-[#FF3B30] text-white',
      'Excluído': 'bg-red-800 text-white'
    };
    return colors[status] || 'bg-gray-200';
  };

  const filtered = documents.filter((doc) => {
    const matchesSearch = !search || 
      doc.title?.toLowerCase().includes(search.toLowerCase()) ||
      doc.tipo?.toLowerCase().includes(search.toLowerCase()) ||
      doc.client_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Documentos</h1>
          <p className="text-[#6E6E73]">Gestão de documentos</p>
        </div>
        <Button onClick={() => navigate('/documents/new')} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-document-button">
          <Plus className="w-4 h-4 mr-2" />
          Novo Documento
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-5 h-5 text-[#6E6E73] flex-shrink-0" />
          <Input
            placeholder="Buscar por título, tipo ou cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
            data-testid="document-search-input"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="document-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="Rascunho">Rascunho</SelectItem>
              <SelectItem value="Em Revisão">Em Revisão</SelectItem>
              <SelectItem value="Aprovado">Aprovado</SelectItem>
              <SelectItem value="Finalizado">Finalizado</SelectItem>
              <SelectItem value="Arquivado">Arquivado</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              data-testid="toggle-show-deleted"
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                showDeleted 
                  ? 'bg-red-50 border-red-300 text-red-700' 
                  : 'bg-white border-[#E5E5EA] text-[#6E6E73] hover:bg-[#F7F7F8]'
              }`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {showDeleted ? 'Excluídos' : 'Ver excluídos'}
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="divide-y divide-[#E5E5EA] border border-[#E5E5EA] rounded-lg overflow-hidden">
            {filtered.map((doc) => (
              <div key={doc.document_id} onClick={() => navigate(`/documents/${doc.document_id}`)} className="p-4 hover:bg-[#FFF5F5] cursor-pointer transition-colors" data-testid={`document-${doc.document_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="bg-[#FFF1F0] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <FileText className="w-5 h-5 text-[#AF2628]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#111111] mb-1">{doc.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-[#6E6E73] flex-wrap">
                        <span>{doc.tipo}</span>
                        <span>·</span>
                        <span>{doc.client_name}</span>
                        <span>·</span>
                        <span>{new Date(doc.updated_at).toLocaleDateString('pt-BR')}</span>
                        {doc.assigned_to_name && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1 text-[#2B5C8A] font-medium">
                              <User className="w-3 h-3" />
                              {doc.assigned_to_name}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                      {doc.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73] mb-4">{search || statusFilter !== 'all' ? 'Nenhum documento encontrado com os filtros aplicados' : 'Nenhum documento criado ainda'}</p>
            {!search && statusFilter === 'all' && (
              <Button onClick={() => navigate('/documents/new')} className="bg-[#AF2628] hover:bg-[#961B22]">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Documento
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
